import { DataPackageMap, Unsafe } from "@intentsify/types";
import { UseQueryOptions, useQueryClient } from "@tanstack/react-query";
import { Endpoints, apiService } from "api";
import { Duration } from "luxon";
import { AsyncReturnType } from "type-fest";

const getCompanyDataPackage = async (companyId: string) => {
  const { data } = await apiService.get<DataPackageMap>(
    Endpoints.Companies.Get.DataPackage(companyId)
  );

  return data;
};

// Normalize companyId to a string so the key is stable whether callers pass
// a route param (string) or company.companyId (number) — otherwise
// invalidation by a number won't match a query created with a string.
const queryKey = (companyId: Unsafe<string | number>) => [
  "getCompanyDataPackage",
  String(companyId),
];

type UseCompanyDataPackageQueryOptions = UseQueryOptions<
  AsyncReturnType<typeof getCompanyDataPackage>
>;

export const useCompanyDataPackageQueryOptions = (
  companyId: Unsafe<string | number>,
  queryOptions?: UseCompanyDataPackageQueryOptions
) =>
  ({
    queryFn: () => getCompanyDataPackage(String(companyId)),
    queryKey: queryKey(companyId),
    staleTime: Duration.fromObject({ minutes: 5 }).as("millisecond"),
    ...queryOptions,
  } satisfies UseCompanyDataPackageQueryOptions);

export const useInvalidateCompanyDataPackage = () => {
  const queryClient = useQueryClient();

  return (companyId: Unsafe<string | number>) =>
    queryClient.invalidateQueries({
      queryKey: queryKey(companyId),
    });
};
